import { ColDef, ICellRendererParams, PostSortRowsParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { aggridHeaderClass, AggridFileForm, aggridFileFormWrapperStyle } from 'src/components/common/common';
import { GridCalendar } from 'src/components/common/form/GridCalendar';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import useUiuxStore from 'src/store/useUiuxStore';
import { useDetailTableWithCodeSearch } from 'src/hooks/detail-table/useDetailTableWithCodeSearch';
import {
	createCalendarCellClickHandler,
	CreateCellClickHandler,
	createCellCssClassRules,
	CustomCellClickAction,
	GridCellConfig,
	postSortHandler,
} from 'src/utils/grid/GridHandlerContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { formatDate, validateString } from 'src/utils/string/stringUtil';
import { downloadExcel } from 'src/utils/blob/blobUtil';
import { showWarning } from 'src/utils/toast/toastUtil';
import { downloadEQM3020_R01File } from './Api_EQM3020_R01';
import { EQM3020_R01Dto, defaultEQM3020_R01Dto } from './Dto_EQM3020_R01';
import { useEQM3020_R01Store, useEQM3020_R01Store_GridCalendar } from './Store_EQM3020_R01';
import { useEQM3020_R01CellChange } from './hooks/EQM3020_R01CellChange';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

/** 로컬스토리지 컬럼 너비·순서 저장 키 */
const COLUMN_STATE_KEY = 'EQM3020_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<EQM3020_R01Dto> | null>;
}

/**
 * EQM3020 검교정 결과 등록 그리드
 * - 조회된 계측기별 행에 교정 결과 입력·수정
 * - 날짜는 GridCalendar, 담당자는 코드검색 모달
 */
export const Table_EQM3020_R01 = ({ clickable, setClickable, gridRef }: Props) => {
	const {
		EQM3020_R01List, setEQM3020_R01List,
		updateEQM3020_R01List,
		deletedEQM3020_R01List,
		selectedEQM3020_R01List, setSelectedEQM3020_R01List,
		initSelectedEQM3020_R01List, allSelectedEQM3020_R01List,
		originalEQM3020_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useEQM3020_R01Store();

	const { EQM3020_R01GridCalendarState, setEQM3020_R01GridCalendarState } = useEQM3020_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = EQM3020_R01GridCalendarState;

	const { setCodeSearchModalProps } = useUiuxStore();
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	/** 그리드 외부 file input (셀 리렌더 시 input 파괴 방지) */
	const fileInputRef = useRef<HTMLInputElement>(null);
	const pendingFileRowRef = useRef<number | null>(null);

	/** 행 ID·패딩·순번 렌더·컬럼 병합 등 그리드 공통 유틸 */
	const {
		getRowId, createRowClassRules, createCellClassRules,
		paddedGridData, renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: EQM3020_R01List,
		defaultGridData: defaultEQM3020_R01Dto,
		selectedRow: selectedEQM3020_R01List,
		originalGridData: originalEQM3020_R01List,
		updateRow: (index, data) => updateEQM3020_R01List(index, data),
	});

	/** 컬럼 상태 복원 시 사용할 헤더명 매핑 */
	const headerNames: Record<string, string> = {
		rowIndex: '', compNm: '사업장', equipmentCd: '계측기코드', equipmentNm: '계측기명',
		calibPlanDt: '교정예정일', calibDt: '교정일자', empNo: '담당자', empNm: '담당자명',
		passYn: '합격', nextCalibDt: '차기교정일', resultFileId: '결과파일등록', remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['compNm', 'equipmentCd', 'equipmentNm', 'calibPlanDt', 'empNm'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['compNm', 'equipmentCd', 'equipmentNm', 'calibPlanDt', 'empNm'],
		pointerCells: ['calibDt', 'empNo', 'passYn', 'nextCalibDt', 'resultFileId', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['equipmentNm', 'remark'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	/** 전 컬럼 공통: 선택·삭제·수정·비활성·포인터 커서 스타일 */
	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM3020_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedEQM3020_R01List,
			deletedList: deletedEQM3020_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns, alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedEQM3020_R01List, deletedEQM3020_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
		createCellClassRules,
	]);

	/** 셀 값 변경·코드검색·편집 종료 핸들러 */
	const cellChangeHandlers = useEQM3020_R01CellChange({
		EQM3020_R01List,
		originalEQM3020_R01List,
		updateEQM3020_R01List,
		gridRef,
		setCodeSearchModalProps,
	});

	/** GridCalendar로 편집하는 날짜 컬럼 */
	const DATE_FIELDS = ['calibDt', 'nextCalibDt'] as const;
	/** 인라인 편집(텍스트·셀렉트) 컬럼 */
	const TEXT_EDITABLE_COLUMNS = ['empNo', 'passYn', 'remark'];

	const editableColumns = [...DATE_FIELDS, ...TEXT_EDITABLE_COLUMNS];

	/** 더블클릭·Enter·방향키 이동 + 담당자 코드검색 */
	const cellEventHandlers = useDetailTableWithCodeSearch<EQM3020_R01Dto>({
		detailList: EQM3020_R01List,
		editableColumns,
		codeSearchConfigs: {
			empNo: {
				category: '담당자',
				codeField: 'empNo',
				responseMapping: { empNo: 'empNo', empNm: 'empNm' },
				clearFields: { empNo: '', empNm: '' },
			},
		},
		settingCodeSearchModalProps: cellChangeHandlers.settingCodeSearchModalProps,
		keyDownConfig: { enableArrowNavigation: true },
	});

	/**
	 * 셀 클릭 시 컬럼별 분기
	 * - 날짜: GridCalendar 오픈
	 * - 그 외 편집 컬럼: 인라인 에디터 시작
	 */
	const cellClickActions: CustomCellClickAction<EQM3020_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => field === 'calibDt',
			action: createCalendarCellClickHandler('calibDt', EQM3020_R01GridCalendarState, setEQM3020_R01GridCalendarState),
		},
		{
			validate: (field) => field === 'nextCalibDt',
			action: createCalendarCellClickHandler('nextCalibDt', EQM3020_R01GridCalendarState, setEQM3020_R01GridCalendarState),
		},
		{
			validate: (field) => TEXT_EDITABLE_COLUMNS.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], [EQM3020_R01GridCalendarState, setEQM3020_R01GridCalendarState]);

	/** cellClickActions를 묶은 AG Grid onCellClicked 핸들러 */
	const handleCellClick = CreateCellClickHandler(EQM3020_R01List, 'valueIndex', cellClickActions);

	/** GridCalendar에서 날짜 선택·초기화 시 행 데이터 반영 */
	const handleCalendarChange = useCallback((date: Date | undefined, rowIndex: number, field: string) => {
		if (rowIndex == null || !field) return;
		const foundData = EQM3020_R01List[rowIndex];
		if (!foundData) return;

		updateEQM3020_R01List(foundData.valueIndex, {
			...foundData,
			[field]: date ? formatDate(date) : '',
			isModified: true,
		});
	}, [EQM3020_R01List, updateEQM3020_R01List]);

	/** 달력 팝업 열릴 때 해당 셀의 기존 날짜를 피커 초기값으로 설정 */
	const handleCalendarLoad = useCallback((rowIndex: number, field: string) => {
		const foundData = EQM3020_R01List[rowIndex];
		if (!foundData) return;

		const dateString = foundData[field as keyof EQM3020_R01Dto] as string;
		if (dateString) {
			const date = new Date(dateString);
			setEQM3020_R01GridCalendarState({ targetDate: !isNaN(date.getTime()) ? date : new Date() });
		} else {
			setEQM3020_R01GridCalendarState({ targetDate: new Date() });
		}
	}, [EQM3020_R01List, setEQM3020_R01GridCalendarState]);

	/** 결과파일 선택·삭제 시 행 데이터 반영 */
	const handleFileChange = useCallback((
		valueIndex: number,
		fileName: string,
		fileData: string,
	) => {
		const foundData = EQM3020_R01List.find((row) => row.valueIndex === valueIndex);
		if (!foundData) return;

		updateEQM3020_R01List(valueIndex, {
			...foundData,
			resultFileNm: fileName,
			resultFileData: fileData,
			resultFileId: fileData ? '' : (fileName ? foundData.resultFileId : ''),
			isModified: true,
		});

		gridRef.current?.api?.refreshCells({ force: true });
	}, [EQM3020_R01List, updateEQM3020_R01List, gridRef]);

	/** 그리드 외부 file input으로 파일 선택 요청 */
	const handleFileSelectRequest = useCallback((valueIndex: number) => {
		pendingFileRowRef.current = valueIndex;
		fileInputRef.current?.click();
	}, []);

	/** 그리드 외부 file input change 핸들러 */
	const handleExternalFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		const valueIndex = pendingFileRowRef.current;
		if (!file || valueIndex == null) return;

		const reader = new FileReader();
		reader.onloadend = () => {
			const result = reader.result as string;
			const base64 = result.replace(/^data:.*?;base64,/, '');
			handleFileChange(valueIndex, file.name, base64);
		};
		reader.readAsDataURL(file);

		if (fileInputRef.current) fileInputRef.current.value = '';
		pendingFileRowRef.current = null;
	}, [handleFileChange]);

	/** 서버에 저장된 결과파일 다운로드 */
	const handleFileDownload = useCallback(async (row: EQM3020_R01Dto) => {
		if (!validateString(row.resultFileId)) {
			showWarning('다운로드할 파일이 없습니다.');
			return;
		}

		try {
			const response = await downloadEQM3020_R01File(row);
			const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
			downloadExcel(blob, row.resultFileNm || `result_file_${row.resultFileId}`);
		} catch {
			showWarning('파일 다운로드 중 오류가 발생했습니다.');
		}
	}, []);

	/** 그리드 기본 컬럼 정의 (순번·계측기 정보·입력 컬럼) */
	const baseColumnDefs: ColDef<EQM3020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			/** 순번 컬럼: 선택·삭제·수정 상태 표시 (행 추가/삭제 버튼 없음) */
			(params) => renderIndexCell(
				params,
				(inner) => getRowStyleFlags(inner, {
					deletedList: deletedEQM3020_R01List,
					selectedList: selectedEQM3020_R01List,
					dataList: EQM3020_R01List,
				}),
				() => {},
				() => {},
			),
		),
		{ headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100, editable: false },
		{ headerName: '계측기코드', colId: 'equipmentCd', field: 'equipmentCd', width: 110, editable: false },
		{ headerName: '계측기명', colId: 'equipmentNm', field: 'equipmentNm', width: 160, editable: false },
		{
			headerName: '교정예정일', colId: 'calibPlanDt', field: 'calibPlanDt', width: 120, editable: false,
			valueFormatter: (params) => (params.data?.calibPlanDt ? formatDate(new Date(params.data.calibPlanDt)) : ''),
			comparator: cellDateComparator,
		},
		{
			headerName: '교정일자', colId: 'calibDt', field: 'calibDt', width: 120, editable: true,
			pasteable: true,
			valueFormatter: (params) => (params.data?.calibDt ? formatDate(new Date(params.data.calibDt)) : ''),
			comparator: cellDateComparator,
		},
		{ headerName: '담당자', colId: 'empNo', field: 'empNo', width: 100, editable: true, pasteable: true },
		{ headerName: '담당자명', colId: 'empNm', field: 'empNm', width: 100, editable: false },
		{
			headerName: '합격', colId: 'passYn', field: 'passYn', width: 80, editable: true,
			cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['Y', 'N'] },
			valueFormatter: (p) => (p.value === 'Y' ? '예' : p.value === 'N' ? '아니오' : ''),
		},
		{
			headerName: '차기교정일', colId: 'nextCalibDt', field: 'nextCalibDt', width: 120, editable: true,
			pasteable: true,
			valueFormatter: (params) => (params.data?.nextCalibDt ? formatDate(new Date(params.data.nextCalibDt)) : ''),
			comparator: cellDateComparator,
		},
		{
			headerName: '결과파일등록', colId: 'resultFileId', field: 'resultFileNm', width: 220, editable: false,
			cellRenderer: (params: ICellRendererParams<EQM3020_R01Dto>) => {
				if (params.node.rowPinned === 'bottom' || !params.data) return null;
				return (
					<AggridFileForm
						fileName={params.data.resultFileNm ?? ''}
						resultFileId={params.data.resultFileId}
						onChange={(_, fileName, fileData) => handleFileChange(params.data!.valueIndex, fileName, fileData)}
						onSelectClick={() => handleFileSelectRequest(params.data!.valueIndex)}
						onDownloadClick={() => handleFileDownload(params.data!)}
						name="resultFile"
						editable={true}
						titleless={true}
						wrapperOption={aggridFileFormWrapperStyle}
						accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
					/>
				);
			},
		},
		{ headerName: '비고', colId: 'remark', field: 'remark', width: 160, editable: true, pasteable: true },
	], [
		deletedEQM3020_R01List, selectedEQM3020_R01List, EQM3020_R01List, renderIndexCell, cellDateComparator,
		handleFileChange, handleFileSelectRequest, handleFileDownload,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	/** baseColumnDefs + 로컬스토리지 columnState 병합 */
	const columnDefs: ColDef<EQM3020_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<EQM3020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames]);

	/** 정렬 후 orderIndex를 원본 순서 기준으로 복원 */
	const handlePostSort = useCallback((params: PostSortRowsParams<EQM3020_R01Dto>) => {
		postSortHandler(params, EQM3020_R01List, originalEQM3020_R01List, setEQM3020_R01List);
	}, [EQM3020_R01List, originalEQM3020_R01List, setEQM3020_R01List]);

	/** Shift+클릭 범위 행 선택 설정 */
	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedEQM3020_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedEQM3020_R01List(ids as string[]),
		/** 행 클릭 선택 가능 여부 (순번 버튼 클릭 직후 1회 차단) */
		isRowSelectable: (event: RowClickedEvent<EQM3020_R01Dto>) => {
			if (!clickable) { setClickable(true); return false; }
			return !!event.data;
		},
	}), [selectedEQM3020_R01List, setSelectedEQM3020_R01List, clickable, setClickable]);

	/** 하단 고정 합계 행 (조회 건수 표시) */
	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		equipmentNm: `${EQM3020_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [EQM3020_R01List]);

	return (
		<GridContextWrapper<EQM3020_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setEQM3020_R01List}
			initSelected={initSelectedEQM3020_R01List}
			defaultRowData={defaultEQM3020_R01Dto}
			allSelected={allSelectedEQM3020_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<EQM3020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<EQM3020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedEQM3020_R01List,
					deletedList: deletedEQM3020_R01List,
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={cellChangeHandlers.handleCellChange}
				onCellEditingStopped={cellChangeHandlers.handleCellEditingStopped}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setEQM3020_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(pos) => setEQM3020_R01GridCalendarState({ pickerPosition: pos })}
				onClear={() => {
					if (targetRowIndex !== null && targetField) {
						handleCalendarChange(undefined, targetRowIndex, targetField);
					}
				}}
				onChange={handleCalendarChange}
				onClickLoad={handleCalendarLoad}
			/>
			<input
				type="file"
				ref={fileInputRef}
				className="hidden"
				accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
				onChange={handleExternalFileChange}
			/>
		</GridContextWrapper>
	);
};
